import type { TFunction } from "i18next";
import { z } from "zod";

// Shipping/address-book text only. Preserve French accents and punctuation;
// reject unsupported scripts without stripping or transliterating the input.
export const SHIPPING_TEXT_PATTERN =
  /^(?:\p{Script=Latin}\p{M}*|[0-9 .,'’&()/#-])*$/u;
export function shippingTextSchema(
  message = "Use English or French letters, numbers, spaces, and standard punctuation."
) {
  return z.string().regex(SHIPPING_TEXT_PATTERN, message);
}

export class ShippingTextError extends Error {
  constructor(
    readonly party: "Sender" | "Recipient",
    readonly field: "name" | "company" | "line1" | "line2" | "city",
    detail: string
  ) {
    super(`${party} ${field}: ${detail}`);
  }

  localizedMessage(t: TFunction): string {
    const party = t(
      this.party === "Sender" ? "orderDetails.sender" : "orderDetails.recipient"
    );
    return `${party} ${t(`validation.shippingTextFields.${this.field}`)}: ${t("validation.shippingText")}`;
  }
}

export function validateShippingAddress(
  address: {
    name?: string;
    company?: string;
    line1: string;
    line2?: string;
    city: string;
  },
  label: "Sender" | "Recipient"
): void {
  for (const field of ["name", "company", "line1", "line2", "city"] as const) {
    const value = address[field];
    if (value === undefined) continue;
    const result = shippingTextSchema().safeParse(value);
    if (!result.success)
      throw new ShippingTextError(label, field, result.error.issues[0].message);
  }
}
